import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../../components/ui/alert-dialog";
import { Plus, Pencil, Trash2, Eye } from "lucide-react";

export default function AdminProperties() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api
      .get("/properties?active_only=false")
      .then((r) => setItems(r.data))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const remove = async (id) => {
    try {
      await api.delete(`/properties/${id}`);
      toast.success("Property deleted.");
      load();
    } catch {
      toast.error("Delete failed.");
    }
  };

  return (
    <div data-testid="admin-properties-page">
      <div className="flex items-end justify-between mb-8">
        <div>
          <p className="overline mb-2">Manage</p>
          <h1 className="font-serif-luxe text-5xl leading-none">Properties</h1>
        </div>
        <Link
          to="/admin/properties/new"
          data-testid="add-property-btn"
          className="inline-flex items-center gap-2 px-5 py-3 bg-[#D4AF37] text-[#121212] text-xs tracking-[0.24em] uppercase hover:bg-[#B3922E] transition-all"
        >
          <Plus size={14} /> Add property
        </Link>
      </div>

      {loading ? (
        <p className="text-[#A3A3A3]">Loading…</p>
      ) : items.length === 0 ? (
        <p className="text-[#A3A3A3]" data-testid="admin-properties-empty">
          No properties yet. Add your first listing.
        </p>
      ) : (
        <div className="border border-[#F5F5DC]/10">
          <table className="w-full text-sm" data-testid="admin-properties-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-[0.2em] text-[#A3A3A3] border-b border-[#F5F5DC]/10">
                <th className="px-4 py-4">Property</th>
                <th className="px-4 py-4 hidden md:table-cell">Location</th>
                <th className="px-4 py-4 hidden md:table-cell">Price</th>
                <th className="px-4 py-4">Status</th>
                <th className="px-4 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr
                  key={p.id}
                  className="border-b border-[#F5F5DC]/5 hover:bg-[#1C1C1E]/40"
                  data-testid={`admin-property-row-${p.id}`}
                >
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-3">
                      <img
                        src={p.images?.[0]}
                        alt=""
                        className="w-14 h-14 object-cover"
                      />
                      <div>
                        <p className="font-serif-luxe text-lg leading-tight">
                          {p.name}
                        </p>
                        <p className="text-xs text-[#737373]">{p.bedrooms} BR · Sleeps {p.max_guests}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-4 hidden md:table-cell text-[#A3A3A3]">
                    {p.location}
                  </td>
                  <td className="px-4 py-4 hidden md:table-cell text-[#D4AF37]">
                    ${p.price_per_night}
                  </td>
                  <td className="px-4 py-4">
                    <span
                      className={`text-[10px] tracking-[0.2em] uppercase px-2 py-1 border ${
                        p.active
                          ? "border-[#32D74B]/40 text-[#32D74B]"
                          : "border-[#737373] text-[#737373]"
                      }`}
                    >
                      {p.active ? "Live" : "Hidden"}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Link
                        to={`/properties/${p.id}`}
                        target="_blank"
                        className="p-2 hover:text-[#D4AF37]"
                        title="View"
                        data-testid={`view-${p.id}`}
                      >
                        <Eye size={16} />
                      </Link>
                      <Link
                        to={`/admin/properties/${p.id}/edit`}
                        className="p-2 hover:text-[#D4AF37]"
                        title="Edit"
                        data-testid={`edit-${p.id}`}
                      >
                        <Pencil size={16} />
                      </Link>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <button
                            className="p-2 hover:text-[#FF453A]"
                            title="Delete"
                            data-testid={`delete-${p.id}`}
                          >
                            <Trash2 size={16} />
                          </button>
                        </AlertDialogTrigger>
                        <AlertDialogContent className="bg-[#1C1C1E] border-[#F5F5DC]/10">
                          <AlertDialogHeader>
                            <AlertDialogTitle className="font-serif-luxe">
                              Delete “{p.name}”?
                            </AlertDialogTitle>
                            <AlertDialogDescription>
                              This will remove the property from the public site. This can't be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel className="bg-transparent border-[#F5F5DC]/20">
                              Cancel
                            </AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => remove(p.id)}
                              className="bg-[#FF453A] hover:bg-[#FF453A]/80 text-white"
                              data-testid={`confirm-delete-${p.id}`}
                            >
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
